// ==============================================================================================
// SAUVEGARDES — le dépôt, le réglage, et ce qui reste à construire.
//
// ⚠️ 🔴 CET ÉCRAN N'EXISTE PAS POUR MONTRER DES SUCCÈS, MAIS POUR RENDRE LES ABSENCES VISIBLES. Une
// sauvegarde qui s'arrête ne produit NI erreur, NI alerte, NI rien : elle cesse d'exister, et on le
// découvre le jour où on la cherche — c'est-à-dire le seul jour où elle comptait.
//
// ⚠️ TROIS ONGLETS, et le troisième DIT qu'il n'est pas construit plutôt que de faire semblant
// (§23.0). Ce qui bloque la restauration comparée n'est PAS une question d'identifiants — un dump est
// la même base à un instant antérieur, ses identifiants sont les siens. C'est que **« absente du
// vivant » a deux sens que rien ne distingue** : une ligne PERDUE (l'incident qu'on répare) et une
// ligne SUPPRIMÉE EXPRÈS (effacement RGPD, fusion, correction). Réinjecter la seconde annule une
// décision — et ça vaut pour TOUTES les tables. S'y ajoutent l'ordre d'insertion et les projections
// (solde de cagnotte, niveaux de stock), qu'une réinjection laisse fausses sans que ça se voie.
//
// ⚠️ LA RESTAURATION EXISTE POURTANT, et l'écran le dit : `deploy/restaurer-base.sh`, éprouvé sur la
// bêta. Ce qui manque est le COMPARATIF, pas le moyen de restaurer.
// ==============================================================================================
import { useCallback, useEffect, useState } from 'react';
import { EmptyState, Icon } from '@orgazone/ui-web';
import {
  api,
  type BackupConfigState, type BackupHistory, type BackupPiece, type BackupState,
} from '../api';
import { TableScroll } from '../table-scroll';
import { BackupHosts, fmtOctets } from './health-backup';

/** Millisecondes → « 1 m 12 s ». Au-delà de la minute on ne montre plus les millisecondes. */
function fmtDuree(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
  return `${Math.floor(s / 60)} m ${String(Math.round(s % 60)).padStart(2, '0')} s`;
}

const ONGLETS = [
  { k: 'depot', label: 'Dépôt' },
  { k: 'restauration', label: 'Restauration' },
  { k: 'reglages', label: 'Réglages' },
] as const;
type Onglet = typeof ONGLETS[number]['k'];

// ── ONGLET DÉPÔT : ce qui est là, pièce par pièce ───────────────────────────────────────────────
function Depot() {
  const [hist, setHist] = useState<BackupHistory | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // ⚠️ UNE SEULE LECTURE À L'OUVERTURE : la donnée ne bouge qu'une fois par nuit. Une minuterie
    //    relirait des milliers de fois la même liste.
    api.backupHistory({ limit: 200 })
      .then((r) => { if (alive) { setHist(r); setErr(null); } })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, []);

  if (err) return <div className="err">Historique illisible : {err}</div>;
  if (!hist) return <div className="muted">Lecture…</div>;

  if (hist.pieces.length === 0) {
    return (
      <EmptyState
        icon="database"
        title="Aucune pièce déposée"
        note="Aucun agent n’a encore déposé de compte rendu détaillé. Une machine qui sauvegarde sans le dire reste invisible ici — et son silence ne se distingue pas d’une sauvegarde arrêtée."
      />
    );
  }

  return (
    <>
      <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginBottom: 10 }}>
        {hist.pieces.length} pièce(s)
        {/* ⚠️ 🔴 LE PLAFOND SE DIT. Une liste tronquée qui ressemble à une liste complète ferait
            conclure « il n'y a que ça » le jour où l'on cherche une archive plus ancienne. */}
        {!hist.complete && (
          <span style={{ color: 'var(--wa-fg)', fontWeight: 700 }}>
            {' '}— liste BORNÉE aux 200 plus récentes, il y en a davantage
          </span>
        )}
      </div>

      {/* ⚠️ `TableScroll` REND LE TABLEAU LUI-MÊME : sans conteneur de défilement, un tableau posé
          dans une carte `overflow: hidden` est COUPÉ à l'écran étroit — la page ne déborde pas,
          aucune barre n'apparaît, et les colonnes de droite deviennent inatteignables (§23.0). */}
      <TableScroll minWidth={760}>
        <thead>
          <tr style={{ background: 'var(--ink-50)' }}>
            {['Base', 'Machine', 'Prise le', 'Taille', 'Durée', 'Verdict', 'Fichier'].map((t) => (
              <th
                key={t}
                className="mono"
                style={{
                  textAlign: 'left', padding: '8px 12px', fontSize: 'var(--t-micro)',
                  color: 'var(--ink500)', fontWeight: 600, whiteSpace: 'nowrap',
                }}
              >
                {t}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hist.pieces.map((p, i) => <Piece key={`${p.at}-${p.host}-${p.database}-${i}`} p={p} />)}
        </tbody>
      </TableScroll>
    </>
  );
}

function Piece({ p }: { p: BackupPiece }) {
  const cell = {
    padding: '7px 12px', fontSize: 'var(--t-meta)', whiteSpace: 'nowrap' as const,
    borderTop: '1px solid var(--ink100)',
  };
  return (
    <>
      <tr style={{ background: p.ok ? 'transparent' : 'var(--da-bg)' }}>
        <td className="mono" style={{ ...cell, fontWeight: 600 }}>{p.database}</td>
        <td className="mono" style={{ ...cell, color: 'var(--ink500)' }}>{p.host}</td>
        <td className="mono" style={{ ...cell, color: 'var(--ink600)' }}>
          {new Date(p.at).toLocaleString('fr-FR')}
        </td>
        {/* ⚠️ « NON MESURÉ » N'EST PAS « ZÉRO » : une archive de 0 o est une panne, une taille inconnue
            ne l'est pas. Les confondre inventerait une alarme, ou en cacherait une. */}
        <td className="mono" style={{ ...cell, color: 'var(--ink700)' }}>
          {p.bytes != null ? fmtOctets(p.bytes) : '—'}
        </td>
        <td className="mono" style={{ ...cell, color: 'var(--ink500)' }}>
          {p.durationMs != null ? fmtDuree(p.durationMs) : '—'}
        </td>
        <td style={cell}>
          <span
            className="mono"
            style={{
              fontSize: 'var(--t-micro)', fontWeight: 700,
              color: p.ok ? 'var(--ok-fg)' : 'var(--da-fg)',
            }}
          >
            {p.ok ? 'vérifiée' : 'ÉCHEC'}
          </span>
        </td>
        <td className="mono" style={{ ...cell, color: 'var(--ink400)', fontSize: 'var(--t-micro)' }}>
          {p.file ?? '—'}
        </td>
      </tr>
      {!p.ok && (
        // Le motif RÉEL de CETTE pièce (§23.0) — c'est lui qui dit où aller réparer.
        <tr>
          <td colSpan={7} className="mono" style={{ padding: '0 12px 7px', fontSize: 'var(--t-micro)', color: 'var(--da-fg)' }}>
            {p.error ?? 'échec sans motif transmis'}
          </td>
        </tr>
      )}
    </>
  );
}

// ── ONGLET RÉGLAGES : ce que la Console déclare, et que l'agent applique ─────────────────────────
const DESTINATIONS = [
  { k: 'local', nom: 'Disque local', icon: 'save', quoi: 'Sur le serveur de bases lui-même. Le plus rapide à écrire, le premier à disparaître avec la machine.' },
  { k: 'smb', nom: 'Partage réseau SMB', icon: 'server', quoi: 'Un NAS du même site. Sort la sauvegarde de la machine, pas du bâtiment.' },
  // ⚠️ SFTP ET JAMAIS FTP : un FTP en clair transporte les identifiants ET le contenu des bases sur
  //    le réseau. Le refus vit aussi côté serveur — une liste d'écran ne protège que l'écran.
  { k: 'sftp', nom: 'SFTP', icon: 'download', quoi: 'Dépôt distant chiffré. Jamais de FTP en clair : il transporterait vos identifiants et vos bases à découvert.' },
  { k: 's3', nom: 'Dépôt objets S3', icon: 'box', quoi: 'Hors des machines de production, avec verrou d’objet. La seule parade au dégât logique.' },
] as const;

const HEURES = [0, 1, 2, 3, 4, 23];
const RETENTIONS = [3, 7, 14, 30];

function Reglages({ config, agentVersion, onSaved }: {
  config: BackupConfigState; agentVersion: string; onSaved: (c: BackupConfigState) => void;
}) {
  const [brouillon, setBrouillon] = useState<BackupConfigState>(config);
  const [etat, setEtat] = useState<{ note?: string; err?: string; envoi?: boolean }>({});

  const modifie = JSON.stringify(brouillon) !== JSON.stringify(config);

  const enregistrer = useCallback(() => {
    setEtat({ envoi: true });
    api.setBackupConfig(brouillon)
      .then((r) => { setEtat({ note: r.note }); onSaved(r.config); })
      // ⚠️ LE MESSAGE RÉEL DU SERVEUR, jamais un « échec » générique : c'est lui qui dit QUELLE
      //    valeur a été refusée, et un refus qu'on ne s'explique pas se contourne au hasard.
      .catch((e) => setEtat({ err: (e as Error).message }));
  }, [brouillon, onSaved]);

  const champ = (k: keyof BackupConfigState, v: string | number) =>
    setBrouillon((b) => ({ ...b, [k]: v }));

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 780 }}>
      <section>
        <h3 style={{ margin: '0 0 4px', fontSize: 'var(--t-body)', fontWeight: 800 }}>Destination des dumps</h3>
        <p style={{ margin: '0 0 10px', fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
          Un dépôt local est le plus rapide à écrire et le premier à disparaître avec la machine. Il ne
          dispense jamais d’une copie hors des serveurs de production.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 8 }}>
          {DESTINATIONS.map((d) => {
            const on = brouillon.destinationKind === d.k;
            return (
              <button
                key={d.k}
                type="button"
                onClick={() => champ('destinationKind', d.k)}
                style={{
                  textAlign: 'left', padding: 12, borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--ok-fg)' : 'var(--ink200)'}`,
                  background: on ? 'var(--ok-bg)' : 'transparent',
                  color: 'var(--ink900)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span aria-hidden="true" style={{
                    width: 28, height: 28, borderRadius: 9, display: 'grid', placeItems: 'center', flexShrink: 0,
                    background: on ? 'color-mix(in srgb, var(--ok-fg) 18%, transparent)' : 'var(--ink100)',
                    color: on ? 'var(--ok-fg)' : 'var(--ink500)',
                  }}>
                    <Icon name={d.icon} size={15} />
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 'var(--t-meta)' }}>{d.nom}</span>
                </div>
                <div style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginTop: 8, lineHeight: 1.45 }}>
                  {d.quoi}
                </div>
              </button>
            );
          })}
        </div>

        <label style={{ display: 'block', marginTop: 12 }}>
          <span style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink600)' }}>Chemin ou URL du dépôt</span>
          <input
            id="backup-destination"
            className="mono"
            value={brouillon.destination}
            onChange={(e) => champ('destination', e.target.value)}
            style={{
              width: '100%', marginTop: 5, minHeight: 36, padding: '0 12px', boxSizing: 'border-box',
              borderRadius: 'var(--r-ctrl)', border: '1px solid var(--ink200)',
              background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-meta)',
            }}
          />
        </label>
        {/* ⚠️ 🔴 AUCUN IDENTIFIANT NE SE SAISIT ICI, ET ON DIT POURQUOI : un compte SMB ou une clé S3
            stockés dans la Console seraient exactement ce qu'un attaquant cherche après l'avoir
            atteinte — les clés du dépôt de sauvegardes. Ils restent sur la machine, en 0600. */}
        <p style={{ margin: '8px 0 0', fontSize: 'var(--t-micro)', color: 'var(--ink500)', lineHeight: 1.5 }}>
          ⚠️ Le compte et la clé d’accès du dépôt ne se saisissent pas ici : ils restent sur la machine,
          dans son fichier d’agent. La Console déclare <b>où</b> déposer, jamais <b>avec quoi</b> s’y
          authentifier.
        </p>
      </section>

      <section>
        <h3 style={{ margin: '0 0 4px', fontSize: 'var(--t-body)', fontWeight: 800 }}>Heure de lancement</h3>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {HEURES.map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => champ('hour', h)}
              className="mono"
              style={{
                minHeight: 32, padding: '0 13px', borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
                border: `1px solid ${brouillon.hour === h ? 'var(--ok-fg)' : 'var(--ink200)'}`,
                background: brouillon.hour === h ? 'var(--ok-bg)' : 'transparent',
                color: brouillon.hour === h ? 'var(--ok-fg)' : 'var(--ink600)',
                fontSize: 'var(--t-meta)', fontWeight: 600,
              }}
            >
              {String(h).padStart(2, '0')}h00
            </button>
          ))}
        </div>
        {/* ⚠️ 🔴 L'ASYMÉTRIE SE DIT, SINON L'ÉCRAN MENT : l'agent lit sa configuration au DÉBUT de son
            passage, donc destination et rétention valent pour la nuit qui vient. L'heure ne peut pas
            — le cron aura déjà déclenché. Afficher un changement d'heure comme appliqué serait faux. */}
        <p style={{ margin: '8px 0 0', fontSize: 'var(--t-micro)', color: 'var(--wa-fg)', lineHeight: 1.5 }}>
          ⚠️ Un changement d’heure ne prend effet qu’<b>au passage suivant</b> : l’agent réécrit sa tâche
          planifiée quand il tourne, et celle de cette nuit est déjà programmée.
        </p>
      </section>

      <section>
        <h3 style={{ margin: '0 0 4px', fontSize: 'var(--t-body)', fontWeight: 800 }}>Rétention au dépôt</h3>
        <p style={{ margin: '0 0 8px', fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
          Nombre d’archives gardées <b>par base</b> — jamais « les N plus récentes » toutes bases
          mélangées, qui effacerait un petit magasin derrière les prises d’un gros.
        </p>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {RETENTIONS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => champ('keep', n)}
              className="mono"
              style={{
                minHeight: 32, padding: '0 13px', borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
                border: `1px solid ${brouillon.keep === n ? 'var(--ok-fg)' : 'var(--ink200)'}`,
                background: brouillon.keep === n ? 'var(--ok-bg)' : 'transparent',
                color: brouillon.keep === n ? 'var(--ok-fg)' : 'var(--ink600)',
                fontSize: 'var(--t-meta)', fontWeight: 600,
              }}
            >
              {n} archives
            </button>
          ))}
        </div>
      </section>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={enregistrer}
          disabled={!modifie || etat.envoi}
          className="btn"
          style={{ opacity: modifie && !etat.envoi ? 1 : 0.5, cursor: modifie && !etat.envoi ? 'pointer' : 'not-allowed' }}
        >
          {etat.envoi ? 'Enregistrement…' : 'Enregistrer le réglage'}
        </button>
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>
          ce dépôt livre l’agent v{agentVersion}
        </span>
      </div>

      {etat.err && <div className="err">{etat.err}</div>}
      {etat.note && (
        <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ok-fg)', lineHeight: 1.5 }}>
          {etat.note}
        </div>
      )}
    </div>
  );
}

// ── ONGLET RESTAURATION : ce qui existe, et ce qui n'est pas construit ───────────────────────────
function Restauration() {
  return (
    <div style={{ maxWidth: 720, display: 'grid', gap: 14 }}>
      {/* ⚠️ 🔴 ON DIT QUE ÇA N'EST PAS CONSTRUIT PLUTÔT QUE D'OFFRIR UN BOUTON QUI FERMERAIT (§23.0).
          Et on dit ce qui EXISTE, sinon l'écran laisserait croire qu'on ne sait pas restaurer. */}
      <div
        style={{
          padding: 14, borderRadius: 'var(--r-ctrl)',
          background: 'var(--wa-bg)', border: '1px solid var(--wa-fg)',
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 'var(--t-meta)', color: 'var(--wa-fg)' }}>
          Le comparatif table par table n’est pas construit
        </div>
        <p style={{ margin: '7px 0 0', fontSize: 'var(--t-micro)', color: 'var(--wa-fg)', lineHeight: 1.6 }}>
          <b>« Absente du vivant » a deux sens que rien ne distingue.</b> Une ligne présente dans la
          sauvegarde et absente aujourd’hui, c’est soit une ligne <b>perdue</b> — l’incident qu’on
          répare — soit une ligne <b>supprimée exprès</b> : un effacement RGPD, une fusion de fiches,
          une correction. Réinjecter la seconde annule une décision, et ça vaut pour <b>toutes</b> les
          tables sans exception.
        </p>
        <p style={{ margin: '9px 0 0', fontSize: 'var(--t-micro)', color: 'var(--wa-fg)', lineHeight: 1.6 }}>
          S’y ajoutent l’<b>ordre d’insertion</b> (réinjecter un enfant sans son parent échoue ou crée
          un orphelin) et les <b>projections</b> : le solde de cagnotte et les niveaux de stock sont
          dérivés de leurs grands livres. Réinjecter les lignes sans les recalculer laisse un solde
          faux — et un solde faux ne se voit pas.
        </p>
      </div>

      <div style={{ padding: 14, borderRadius: 'var(--r-ctrl)', border: '1px solid var(--ink200)' }}>
        <div style={{ fontWeight: 800, fontSize: 'var(--t-meta)' }}>Ce qui existe, et qui a été éprouvé</div>
        <p style={{ margin: '7px 0 0', fontSize: 'var(--t-micro)', color: 'var(--ink500)', lineHeight: 1.6 }}>
          <span className="mono" style={{ color: 'var(--ink700)' }}>deploy/restaurer-base.sh</span> ramène
          une base depuis une archive, <b>à côté</b> de la base vivante et jamais par-dessus
          (<span className="mono">&lt;base&gt;_restore_&lt;horodatage&gt;</span>) : on compte, on compare,
          puis on décide. Les rôles se restaurent d’abord, sans quoi tout échoue sur des propriétaires
          inconnus. <span className="mono">--ecraser</span> existe, exige une confirmation tapée et un
          terminal.
        </p>
        <p style={{ margin: '9px 0 0', fontSize: 'var(--t-micro)', color: 'var(--ink500)', lineHeight: 1.6 }}>
          ⚠️ Une restauration logique rend l’état <b>du moment de l’archive</b> : jusqu’à 24 h de ventes
          manquent, et ces heures-là se récupèrent depuis la <b>flotte</b> — les caisses gardent leurs
          pièces — pas depuis le dépôt.
        </p>
      </div>
    </div>
  );
}

export function Backups() {
  const [onglet, setOnglet] = useState<Onglet>('depot');
  const [state, setState] = useState<BackupState | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.backups()
      .then((r) => { if (alive) { setState(r); setErr(null); } })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, []);

  const majConfig = useCallback((config: BackupConfigState) => {
    setState((s) => (s ? { ...s, config } : s));
  }, []);

  return (
    <div className="page">
      <h1 className="page-title">Sauvegardes</h1>
      <p style={{ margin: '6px 0 0', fontSize: 'var(--t-meta)', color: 'var(--ink500)', maxWidth: 760, lineHeight: 1.5 }}>
        Un dump par base et par nuit, relu aussitôt écrit. Une sauvegarde qui s’arrête ne produit ni
        erreur ni alerte : elle cesse d’exister. Ce que cet écran doit montrer, ce sont les absences.
      </p>

      {err && <div className="err" style={{ marginTop: 14 }}>État des sauvegardes illisible : {err}</div>}

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--ink200)', margin: '16px 0', flexWrap: 'wrap' }}>
        {ONGLETS.map((o) => (
          <button aria-pressed={onglet === o.k}
            key={o.k}
            type="button"
            onClick={() => setOnglet(o.k)}
            style={{
              minHeight: 36, padding: '0 14px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 'var(--t-body)', fontWeight: onglet === o.k ? 700 : 500,
              color: onglet === o.k ? 'var(--ink900)' : 'var(--ink500)',
              borderBottom: `2px solid ${onglet === o.k ? 'var(--ok-fg)' : 'transparent'}`,
            }}
          >
            {o.label}
          </button>
        ))}
      </div>

      {onglet === 'depot' && (
        <>
          {/* L'état par hôte AVANT la liste des pièces : « est-ce que ça tourne » se lit d'un coup
              d'œil, « quelle archive reprendre » se cherche ensuite. */}
          {state && <BackupHosts state={state} />}
          <div style={{ marginTop: 16 }}><Depot /></div>
        </>
      )}

      {onglet === 'restauration' && <Restauration />}

      {onglet === 'reglages' && state && (
        <Reglages config={state.config} agentVersion={state.agentVersion} onSaved={majConfig} />
      )}
      {onglet === 'reglages' && !state && !err && <div className="muted">Lecture…</div>}
    </div>
  );
}
